import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { MaterialCard } from "@/components/MaterialCard";

import { BACKEND_URL } from "../utils/backendUrl";
const API = `${BACKEND_URL}/api`;

const CatalogPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  // Initialize from URL ?brand= to avoid race condition with first fetch
  const [searchQuery, setSearchQuery] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("brand") || "";
  });
  const [favorites, setFavorites] = useState([]);
  const { user, token } = useAuth();

  // Sync search input when URL ?brand= changes (e.g. user clicks a different brand link)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const brand = params.get("brand");
    if (brand) setSearchQuery(brand);
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    const fetchMaterials = async () => {
      try {
        const url = searchQuery 
          ? `${API}/materials?search=${encodeURIComponent(searchQuery)}`
          : `${API}/materials`;
        const response = await axios.get(url);
        if (!cancelled) {
          setMaterials(response.data.materials || response.data);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error("Error al cargar materiales");
          if (process.env.NODE_ENV === 'development') console.debug('CatalogPage fetch failed:', error.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMaterials();
    return () => { cancelled = true; };
  }, [searchQuery]);

  useEffect(() => {
    const fetchFavorites = async () => {
      if (user && token) {
        try {
          const response = await axios.get(`${API}/favorites/${user.id}`);
          setFavorites(response.data.favorites.map(f => f.material_id));
        } catch (error) {
          console.error(error);
        }
      }
    };
    fetchFavorites();
  }, [user, token]);

  const toggleFavorite = async (materialId) => {
    if (!user) {
      toast.error("Inicia sesión para guardar favoritos");
      return;
    }
    try {
      if (favorites.includes(materialId)) {
        await axios.delete(`${API}/favorites/${user.id}/${materialId}`);
        setFavorites(favorites.filter(id => id !== materialId));
        toast.success("Eliminado de favoritos");
      } else {
        await axios.post(`${API}/favorites`, { user_id: user.id, material_id: materialId });
        setFavorites([...favorites, materialId]);
        toast.success("Añadido a favoritos");
      }
    } catch (error) {
      toast.error("Error al actualizar favoritos");
    }
  };

  const filteredMaterials = filter === "all" 
    ? materials 
    : materials.filter(m => m.category === filter);

  return (
    <div className="min-h-screen pt-24 px-6" data-testid="catalog-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl mb-4">CATÁLOGO DE MATERIALES</h1>
          <p className="text-muted-foreground">Explora todos los materiales disponibles basados en SICNOVA 3D</p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Buscar materiales..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>

        <div className="mb-8">
          <Tabs defaultValue="all" className="w-full" onValueChange={setFilter}>
            <TabsList className="bg-muted/50 p-1 rounded-sm">
              <TabsTrigger value="all" className="rounded-sm" data-testid="filter-all">Todos ({materials.length})</TabsTrigger>
              <TabsTrigger value="filament" className="rounded-sm" data-testid="filter-filament">Filamentos</TabsTrigger>
              <TabsTrigger value="resin" className="rounded-sm" data-testid="filter-resin">Resinas</TabsTrigger>
              <TabsTrigger value="technical" className="rounded-sm" data-testid="filter-technical">Técnicos</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {loading ? (
          <div className="catalog-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="loading-skeleton h-64 rounded-sm" />
            ))}
          </div>
        ) : (
          <div className="catalog-grid">
            {filteredMaterials.map((material) => (
              <MaterialCard
                key={material.id}
                material={material}
                onSelect={() => navigate(`/material/${material.id}`)}
                onFavorite={toggleFavorite}
                isFavorite={favorites.includes(material.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


export default CatalogPage;
